import { createSlice } from '@reduxjs/toolkit'
import { getStudentSurveyById, getStudentSurveyByIdPublic, submitStudentSurvey, submitStudentSurveyPublic } from './apis'
import { type StudentDistribution } from '../../../models/Distribution'
import { type ISurveyTemplate } from '../../../models/SurveyTemplate'
import { notifyError } from '../../../utils/notifyError'
import { formatPublicSurveyHttpError } from '../../../helpers/publicSurveyHttpError'

export interface StudentDistributionSliceType {
  studentSelectedSurvey: ISurveyTemplate | null
  /** Fetching the selected survey. Owns the page-level loading boundary in `SurveyPage`. */
  isLoading: boolean
  /**
   * CR-260: submitting the selected survey. Kept separate from `isLoading` so a submit
   * never trips `SurveyPage`'s fetch loading boundary — that boundary unmounts
   * `SurveyFeature`, and the remount on success re-ran its draft-restore effect and
   * re-showed the "Reloading previous responses" notice after submission.
   */
  isSubmitting: boolean
  /** Set when `getStudentSurveyByIdPublic` fails (e.g. 400 invalid tenant token, 404). */
  publicSurveyLoadError: string | null
}

const initialState: StudentDistributionSliceType = {
  studentSelectedSurvey: null,
  isLoading: false,
  isSubmitting: false,
  publicSurveyLoadError: null
}

const studentDistributionSlice = createSlice({
  name: 'studentDistributions',
  initialState,
  reducers: {
    resetState: (state) => {
      state.publicSurveyLoadError = null
    }
  },
  extraReducers: (builder) => {
    builder

      .addCase(getStudentSurveyByIdPublic.pending, (state) => {
        state.isLoading = true
        state.publicSurveyLoadError = null
      })
      .addCase(getStudentSurveyByIdPublic.fulfilled, (state, action) => {
        state.isLoading = false
        state.publicSurveyLoadError = null
        state.studentSelectedSurvey = action.payload
      })
      .addCase(getStudentSurveyByIdPublic.rejected, (state, action) => {
        state.isLoading = false
        state.studentSelectedSurvey = null
        state.publicSurveyLoadError = formatPublicSurveyHttpError(action.payload)
      })

      // Student single Survey by Id
      .addCase(getStudentSurveyById.pending, (state) => {
        state.isLoading = true
        state.publicSurveyLoadError = null
      })
      .addCase(getStudentSurveyById.fulfilled, (state, action) => {
        state.isLoading = false
        state.studentSelectedSurvey = action.payload
      })
      .addCase(getStudentSurveyById.rejected, (state, action) => {
        state.isLoading = false
      })

      // Submit student survey
      .addCase(submitStudentSurvey.pending, (state) => {
        state.isSubmitting = true
      })
      .addCase(submitStudentSurvey.fulfilled, (state) => {
        state.isSubmitting = false
      })
      .addCase(submitStudentSurvey.rejected, (state) => {
        state.isSubmitting = false
      })
      // Submit student survey public
      .addCase(submitStudentSurveyPublic.pending, (state) => {
        state.isSubmitting = true
      })
      .addCase(submitStudentSurveyPublic.fulfilled, (state) => {
        state.isSubmitting = false
      })
      .addCase(submitStudentSurveyPublic.rejected, (state) => {
        state.isSubmitting = false
      })
  }
})
export const { resetState } = studentDistributionSlice.actions
export default studentDistributionSlice.reducer
